import { useEffect, lazy, Suspense } from "react";
import { Helmet } from "react-helmet-async";
import SalesLetterHero from "@/components/SalesLetterHero";
import SiteHeader from "@/components/SiteHeader";
import ClinicalHeaderBar from "@/components/ClinicalHeaderBar";
import { useMetaPixel, initializeFbcTracking, getExternalId } from "@/hooks/useMetaPixel";
import { useUtmDetection, FunnelStage } from "@/hooks/useUtmDetection";

// Lazy load components below the fold
const ClinicalIntroSection = lazy(() => import("@/components/ClinicalIntroSection"));
const ClinicalComparisonSection = lazy(() => import("@/components/ClinicalComparisonSection"));
const StorySection = lazy(() => import("@/components/StorySection"));
const SolutionSection = lazy(() => import("@/components/SolutionSection"));
const TestimonialsSection = lazy(() => import("@/components/TestimonialsSection"));
const OfferSection = lazy(() => import("@/components/OfferSection"));
const GuaranteeSection = lazy(() => import("@/components/GuaranteeSection"));
const DecisionSection = lazy(() => import("@/components/DecisionSection"));
const FinalCTASection = lazy(() => import("@/components/FinalCTASection"));
const MedicalResourcesFooter = lazy(() => import("@/components/MedicalResourcesFooter"));

interface MetaContent {
  title: string;
  description: string;
}

const META_CONTENT: Record<FunnelStage, MetaContent> = {
  bottom: {
    title: "Remoção de PPP e Glândulas de Tyson: Métodos Clínicos (2026)",
    description: "Comparação clínica entre laser CO₂, eletrocauterização e protocolos caseiros. Custo, recuperação e eficácia baseados em evidências.",
  },
  middle: {
    title: "Tratamento de Pápulas Peroladas Penianas | Visão Clínica",
    description: "Comparação clínica dos métodos de remoção de PPP. Custo, recuperação e eficácia baseados em evidências.",
  },
  top: {
    title: "Glândulas de Tyson e PPP: Guia Clínico e Remoção (2026)",
    description: "Guia clínico sobre PPP e glândulas de Tyson: prevalência, diagnóstico diferencial e opções de tratamento baseadas em evidências.",
  },
};

const Index = () => {
  const { trackPageView } = useMetaPixel();
  const { funnelStage } = useUtmDetection();

  const metaContent = META_CONTENT[funnelStage];

  useEffect(() => {
    // Initialize enhanced fbc tracking (captures from URL, referrer, hash, etc.)
    initializeFbcTracking();
    getExternalId();
    
    // Track page view
    trackPageView();
  }, [trackPageView]);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{metaContent.title}</title>
        <meta name="description" content={metaContent.description} />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={metaContent.title} />
        <meta property="og:description" content={metaContent.description} />
        <meta property="og:url" content="https://smothmethod.com/" />
        <link rel="canonical" href="https://smothmethod.com/" />
        <link rel="alternate" hrefLang="pt-br" href="https://smothmethod.com/" />
        <link rel="alternate" hrefLang="x-default" href="https://smothmethod.com/" />
        <meta property="og:locale" content="pt_BR" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalWebPage",
          "headline": "Glândulas de Tyson e Pápulas Peroladas Penianas (PPP): Visão Clínica e Métodos de Remoção",
          "description": "Guia clínico completo cobrindo identificação, diagnóstico diferencial e opções de tratamento baseadas em evidências para PPP e glândulas de Tyson.",
          "url": "https://smothmethod.com/",
          "inLanguage": "pt-BR",
          "lastReviewed": "2026-01-15",
          "dateModified": "2026-01-15",
          "datePublished": "2024-01-15",
          "mainEntity": {
            "@type": "MedicalCondition",
            "name": "Pápulas Peroladas Penianas",
            "alternateName": ["Hirsuties papillaris genitalis", "Hirsutoid papillomas", "PPP"],
            "epidemiology": "Afeta 14-48% dos homens não circuncidados",
            "signOrSymptom": { "@type": "MedicalSignOrSymptom", "name": "Pequenas pápulas em forma de cúpula ao redor da coroa" }
          },
          "specialty": { "@type": "MedicalSpecialty", "name": "Dermatologia" },
          "medicalAudience": { "@type": "MedicalAudience", "audienceType": "Patient" },
          "publisher": { "@type": "Organization", "name": "Smooth P-nis Method", "logo": { "@type": "ImageObject", "url": "https://smothmethod.com/logo.jpg" } }
        })}</script>
      </Helmet>
      
      {/* Clinical header bar - above the fold for SEO */}
      <ClinicalHeaderBar />
      
      <SiteHeader />
      <main>
        <article className="max-w-2xl lg:max-w-3xl mx-auto px-4 md:px-6 py-6 md:py-8">
          <SalesLetterHero />
          <Suspense fallback={<div className="min-h-screen" />}>
            {/* Clinical sections first for SEO */}
            <ClinicalIntroSection />
            <ClinicalComparisonSection />
            
            {/* Personal story section (moved after clinical content) */}
            <StorySection />
            <SolutionSection />
            <TestimonialsSection />
            <OfferSection />
            <GuaranteeSection />
            <DecisionSection />
            <FinalCTASection />
            
            {/* Medical resources footer */}
            <MedicalResourcesFooter />
          </Suspense>
          
          <footer className="text-center py-4 md:py-6 border-t border-border mt-6 md:mt-8">
            <p className="text-xs text-muted-foreground mb-3">
              © {new Date().getFullYear()} Smooth P-nis Method. Todos os direitos reservados.
            </p>
            <p className="text-xs text-muted-foreground/70 max-w-lg mx-auto mb-3">
              Conteúdo educacional. Resultados podem variar. Consulte um profissional de saúde para orientação médica.
            </p>
            <p className="text-xs text-muted-foreground/70 max-w-lg mx-auto">
              Este site não tem afiliação com o Facebook, Inc. ou a Meta Platforms, Inc.
            </p>
          </footer>
        </article>
      </main>
    </div>
  );
};

export default Index;
